import { Button, SectionItem } from '@mairistem/composants';
import { ListeTable } from '@mairistem/liste';
import _ from 'lodash';
import React from 'react';
import { useHistory } from 'react-router';
import { ProduitContextProvider } from '@/components/Context';
import { REGISSEUR } from '@/constants';
import { ProduitContext } from '@/contexts';
import { baseUrlFront } from '@/routes';
import type { ProduitsListeItemType } from '@/types/produit';
import { formatProduitsToListeItem } from '@/utils';
import { useRegieStore, useRegisseurStore } from '@/zustand';
import ModalDetailProduit from './ModalDetailProduit';
import ProduitsListeTableRow from './ProduitsListeTableRow';

type ProduitsListeProps = {
  // Affiche un tableau avec moins d'informations
  basic?: boolean;
  // Affiche la liste sans éléments (pour la création de fiche régie)
  empty?: boolean;
};

const ProduitsListe = (props: ProduitsListeProps) => {
  const { basic, empty = false } = props;

  const [detailOpen, setDetailOpen] = React.useState<number>(null);
  const [createOpen, setCreateOpen] = React.useState(false);

  const history = useHistory();
  const autonome = useRegieStore((state) => state.regie?.autonome);
  const mandat = useRegisseurStore((state) => state.regisseur?.mandat);
  const canEdit = autonome && mandat === REGISSEUR.TYPE_PRINCIPAL;

  const { produits, loading, refresh } = React.useContext(ProduitContext);

  const selectedProduit = React.useMemo(
    () => _.find(produits, { identifiant: detailOpen }),
    [detailOpen, produits],
  );

  // Formate les données pour l'affichage dans la liste
  // avec seulement les data dont on a besoin
  const formatedProduits = React.useMemo<ProduitsListeItemType[]>(
    () => formatProduitsToListeItem(produits),
    [produits],
  );

  const handleDetailClick = React.useCallback((item: ProduitsListeItemType) => {
    setDetailOpen(item.identifiant);
  }, []);

  const handleSave = React.useCallback(() => {
    refresh();
    setDetailOpen(null);
  }, [refresh]);

  const handleCreate = React.useCallback(() => {
    refresh();
    setCreateOpen(false);
  }, [refresh]);

  const renderRow = (item, __, rowProps) => (
    <ProduitsListeTableRow
      item={item}
      rowProps={rowProps}
      basic={basic}
      autonome={autonome}
      onDetailClick={handleDetailClick}
    />
  );

  return (
    <>
      {!basic && (
        <SectionItem
          title={autonome ? 'Produits encaissables au comptant' : 'Produits encaissés sur facture'}
          single
          compact
        />
      )}

      {canEdit && !basic && !empty && (
        <Button
          data-testid="add-button-produit"
          basic
          icon="add"
          content="Ajouter un produit"
          floated="right"
          onClick={() => setCreateOpen(true)}
          style={{
            marginTop: '0.5em',
            marginBottom: '1em',
          }}
        />
      )}
      <div style={{ height: basic ? '100%' : '95%' }}>
        <ListeTable
          {...(!empty && {
            count: _.size(formatedProduits),
            items: formatedProduits,
          })}
          // @ts-expect-error
          noResultsMessage={
            autonome ? (
              <>
                Les produits peuvent être créés dans les paramètres.
                {!empty && (
                  <>
                    <br />
                    <Button
                      basic
                      size="tiny"
                      content="Accéder aux paramètres"
                      style={{ marginTop: '0.5em' }}
                      onClick={() => history.push(`${baseUrlFront}/parametres`)}
                    />
                  </>
                )}
              </>
            ) : (
              'Aucun produit'
            )
          }
          loading={loading}
        >
          <ListeTable.Header>
            {autonome ? (
              // Produits encaissables au comptant : colonnes du ticket
              // (identiques en Constitution de la régie et dans les Paramètres)
              <>
                <ListeTable.Column verticalAlign="middle" name="libelle">
                  Libellé du produit
                </ListeTable.Column>
                <ListeTable.Column
                  verticalAlign="middle"
                  name="prixUnitaireTTC"
                  textAlign="right"
                  style={{ width: 140 }}
                >
                  Prix unitaire TTC
                </ListeTable.Column>
                <ListeTable.Column
                  verticalAlign="middle"
                  name="tva"
                  textAlign="right"
                  style={{ width: 120 }}
                >
                  Taux de TVA
                </ListeTable.Column>
                <ListeTable.Column verticalAlign="middle" name="article" style={{ width: 130 }}>
                  Article
                </ListeTable.Column>
                <ListeTable.Column
                  verticalAlign="middle"
                  name="codeProduitLocal"
                  style={{ width: 130 }}
                >
                  Code
                  <br />
                  produit local
                </ListeTable.Column>
                <ListeTable.Column
                  verticalAlign="middle"
                  name="payableCesu"
                  textAlign="center"
                  style={{ width: 100 }}
                >
                  CESU
                </ListeTable.Column>
                <ListeTable.Column
                  verticalAlign="middle"
                  name="payableChequeVacances"
                  textAlign="center"
                  style={{ width: 150 }}
                >
                  Chèque vacances
                </ListeTable.Column>
              </>
            ) : basic ? (
              // Produits encaissés sur facture — vue Constitution (comportement d'origine)
              <>
                <ListeTable.Column name="libelle">Libellé du produit</ListeTable.Column>
                <ListeTable.Column name="article" style={{ width: 150 }}>
                  Article
                </ListeTable.Column>
                <ListeTable.Column name="codeProduitLocal" style={{ width: 200 }}>
                  Code produit local
                </ListeTable.Column>
                <ListeTable.Column name="tva" textAlign="right" style={{ width: 150 }}>
                  Taux de TVA
                </ListeTable.Column>
                <ListeTable.Column name="source" textAlign="center">
                  Application source
                </ListeTable.Column>
              </>
            ) : (
              // Produits encaissés sur facture — vue Paramètres (comportement d'origine)
              <>
                <ListeTable.Column verticalAlign="middle" name="libelle">
                  Libellé du produit
                </ListeTable.Column>
                <ListeTable.Column verticalAlign="middle" name="article" style={{ width: 130 }}>
                  Article
                </ListeTable.Column>
                <ListeTable.Column
                  verticalAlign="middle"
                  name="codeProduitLocal"
                  style={{ width: 130 }}
                >
                  Code
                  <br />
                  produit local
                </ListeTable.Column>
                <ListeTable.Column
                  verticalAlign="middle"
                  name="tva"
                  textAlign="right"
                  style={{ width: 120 }}
                >
                  Taux de TVA
                </ListeTable.Column>
                <ListeTable.Column
                  verticalAlign="middle"
                  name="payableCesu"
                  textAlign="center"
                  style={{ width: 100 }}
                >
                  CESU
                </ListeTable.Column>
                <ListeTable.Column
                  verticalAlign="middle"
                  name="payableChequeVacances"
                  textAlign="center"
                  style={{ width: 150 }}
                >
                  Chèque vacances
                </ListeTable.Column>
                <ListeTable.Column verticalAlign="middle" name="source" style={{ width: 200 }}>
                  Application source
                </ListeTable.Column>
              </>
            )}
          </ListeTable.Header>
          {renderRow as unknown as React.ReactNode}
        </ListeTable>
      </div>
      {detailOpen && (
        <ProduitContextProvider type="parametrage-detail" produit={selectedProduit}>
          <ModalDetailProduit
            produit={selectedProduit}
            onClose={() => setDetailOpen(null)}
            onSave={handleSave}
          />
        </ProduitContextProvider>
      )}
      {createOpen && (
        <ModalDetailProduit onClose={() => setCreateOpen(false)} onSave={handleCreate} />
      )}
    </>
  );
};

export default ProduitsListe;
